import { LngLat } from "mapbox-gl";
import { computed } from "mobx";
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from "react";
import { useMap } from "react-map-gl";
import { useNavigate, useParams } from 'react-router-dom';
import { useDealPortfolioStore, usePipedriveStore, usePopupStore, usePropertyStore } from "../..";
import { useDealBoeGenerator } from "../../hooks/boeGenerator/useDealBoeGenerator";
import useClosable from "../../hooks/useClosable";
import { useLoiGenerator } from "../../hooks/useLoiGenerator";
import useProperty from "../../hooks/useProperty";
import useRecentlyViewed from "../../hooks/useRecentlyViewed";
import DetailsType from "../../interfaces/DetailsType";
import { GeoCoordinate } from "../../interfaces/GeoCoordinate";
import IDeal from "../../interfaces/IDeal";
import IProperty from "../../interfaces/IProperty";
import { RecentItemType } from "../../interfaces/IRecentItem";
import GeometryService from "../../services/geometryService";
import { ClosableOrder } from "../../store/ClosableStore";
import { formatPrice } from "../../utils/utils";
import ButtonWithTooltip from "../ButtonWithTooltip";
import commonStyles from '../Common.module.css';
import detailsStyles from '../Details.module.css';
import DetailsPanelWithFlyTo from "../DetailsPanelWithFlyTo";
import { DealFavoriteCheckbox } from "../favorites/FavoriteCheckbox";
import CompanyIcon from "../icons/CompanyIcon";
import ConvertToTextIcon from "../icons/ConvertToTextIcon";
import DealIcon from "../icons/DealIcon";
import DetailsIcon from "../icons/DetailsIcon";
import DropboxIcon from "../icons/DropboxIcon";
import EditIcon from "../icons/EditIcon";
import GoogleMapsIcon from "../icons/GoogleMapsIcon";
import LeaseCompIcon from "../icons/LeaseCompIcon";
import LocationIcon from "../icons/LocationIcon";
import PipedriveIcon from "../icons/PipedriveIcon";
import PortfolioIcon from "../icons/PortfolioIcon";
import PropertyIcon from "../icons/PropertyIcon";
import SaleCompIcon from "../icons/SaleCompIcon";
import TableConvertIcon from "../icons/TableConvertIcon";
import TenantInMarketIcon from "../icons/TenantInMarketIcon";
import IconWithTooltip from "../IconWithTooltip";
import SegmentedButtons from "../SegmentedButtons";
import OnMapSpinner from "../spinners/OnMapSpinner";
import Spinner from "../spinners/Spinner";
import DealDetailsCompaniesTab from "./DealDetailsCompaniesTab";
import DealDetailsDealsTab from "./DealDetailsDealsTab";
import DealDetailsDetailsTab from "./DealDetailsDetailsTab";
import DealDetailsLeaseCompsTab from "./DealDetailsLeaseCompsTab";
import DealDetailsPropertyTab from "./DealDetailsPropertyTab";
import DealDetailsRecordedSalesTab from "./DealDetailsRecordedSalesTab";
import DealDetailsSaleCompsTab from "./DealDetailsSaleCompsTab";
import DealDetailsTenantsInMarketTab from "./DealDetailsTenantsInMarketTab";
import DealPortfolioPopup from "./portfolio/DealPortfolioPopup";

function DealDetails() {
    const { id } = useParams();
    const map = useMap().map;
    const navigate = useNavigate();
    const pipedriveStore = usePipedriveStore();
    const popupStore = usePopupStore();
    const propertyStore = usePropertyStore();
    const dealPortfolioStore = useDealPortfolioStore();
    const deal = useMemo(() => computed(() => pipedriveStore.deals.find(d => d.id.toString() === id)), [id]).get();
    const dealStage = useMemo(() => computed(() => deal ? pipedriveStore.dealStages.find(s => s.id === deal.dealStageId) : null), [deal]).get();
    const portfolio = useMemo(() => computed(() => deal ? dealPortfolioStore.getPortfolioForDeal(deal.id) : null), [deal, dealPortfolioStore.portfolios]).get();
    const [dealTab, setDealTab] = useState(DealTab.Details);
    const [isPortfolioPopupOpen, setIsPortfolioPopupOpen] = useState(false);
    const lngLat = useMemo(() => deal && new LngLat(deal.lon, deal.lat), [deal]);
    const coordinates = useMemo(() => lngLat && new GeoCoordinate(lngLat.lat, lngLat.lng), [lngLat]);
    const geometryService = useMemo(() => new GeometryService(), []);
    const shapeCentroids = useMemo(() => (deal?.dealShapes || []).filter(shape => !!shape.apn?.trim()).map(shape => geometryService.getShapeCenterPoint(shape)), [deal]);
    const { property, fetchProperty } = useProperty(lngLat);
    const [companyCount, setCompanyCount] = useState(0);
    const [leaseCompCount, setLeaseCompCount] = useState(0);
    const [dealCount, setDealCount] = useState(0);
    const [saleCompCount, setSaleCompCount] = useState(0);
    const [recordedSaleCount, setRecordedSaleCount] = useState(0);
    const [tenantInMarketCount, setTenantInMarketCount] = useState(0);
    const segmentedButtons = useMemo(() => [
        {
            value: DealTab.Details,
            label: (
                <IconWithTooltip title="Details">
                    <DetailsIcon color="#25479A" />
                </IconWithTooltip>
            ),
        },
        {
            value: DealTab.PropertyInfo,
            label: (
                <IconWithTooltip title="Property Info">
                    <PropertyIcon color="#25479A" />
                </IconWithTooltip>
            ),
        },
        {
            value: DealTab.Companies,
            label: (
                <IconWithTooltip title="Companies" count={companyCount}>
                    <CompanyIcon color="#25479A" />
                </IconWithTooltip>
            ),
        },
        {
            value: DealTab.LeaseComps,
            label: (
                <IconWithTooltip title="Lease Comps" count={leaseCompCount}>
                    <LeaseCompIcon color="#25479A" />
                </IconWithTooltip>
            ),
        },
        {
            value: DealTab.Deals,
            label: (
                <IconWithTooltip title="Deals" count={dealCount}>
                    <DealIcon color="#25479A" />
                </IconWithTooltip>
            ),
        },
        {
            value: DealTab.SaleComps,
            label: (
                <IconWithTooltip title="Sale Comps" count={saleCompCount}>
                    <SaleCompIcon color="#25479A" />
                </IconWithTooltip>
            ),
        },
        {
            value: DealTab.RecordedSales,
            label: (
                <IconWithTooltip title="Recorded Sales" count={recordedSaleCount}>
                    <LocationIcon color="#25479A" />
                </IconWithTooltip>
            ),
        },
        {
            value: DealTab.TenantsInMarket,
            label: (
                <IconWithTooltip title="Tenants in Market" count={tenantInMarketCount}>
                    <TenantInMarketIcon color="#25479A" />
                </IconWithTooltip>
            ),
        },
    ], [companyCount, leaseCompCount, dealCount, saleCompCount, recordedSaleCount, tenantInMarketCount]);
    useRecentlyViewed(Number(id), RecentItemType.Deal);

    const close = () => navigate("/");
    useClosable(ClosableOrder.Medium, close);

    useEffect(() => {
        if (!map || !lngLat || !property || !shapeCentroids.length)
            return;

        propertyStore.addParcelsToProperty(lngLat, shapeCentroids, map);
    }, [shapeCentroids, lngLat, map, propertyStore, property]);

    useEffect(() => {
        if (deal) {
            fetchProperty();
            popupStore.openDealPopup(deal.id);

            if (!pipedriveStore.dealIdsFileLists[deal.id])
                pipedriveStore.fetchDealFiles(deal.id);
        }

        return () => popupStore.close();
    }, [deal]);

    if (pipedriveStore.isDealsLoading.fetching)
        return <OnMapSpinner />;

    if (!deal || !coordinates)
        return null;

    return (
        <>
            <DetailsPanelWithFlyTo type={DetailsType.DealDetails} coordinates={coordinates} onClose={close} controls={[
                <DealFavoriteCheckbox key="favorite" id={Number(id)} className={commonStyles.clickableIcon} />,
                <PortfolioIcon key="portfolio" color={portfolio ? "#25479A" : "#707070"} onClick={() => setIsPortfolioPopupOpen(true)} />,
                <EditIcon key="edit" onClick={() => navigate(`/deals/${id}/edit`)} />
            ]}>
                <div className={detailsStyles.header}>
                    <div className={detailsStyles.headerGroup}>
                        <DealIcon color="#25479A" />
                        <div className={detailsStyles.title}>
                            <div>{formatPrice(deal.value)} - {dealStage!.name}</div>
                            <span className={commonStyles.link} onClick={e => popupStore.openDealPopup(deal.id)}>{deal.name}</span>
                        </div>
                    </div>
                    <div className={[detailsStyles.headerGroup, commonStyles.hideInLandscape].join(" ")}>
                        <ButtonWithTooltip title="Pipedrive" className={commonStyles.navigationButton} onClick={() => window.open(`https://${pipedriveStore.companyDomain}.pipedrive.com/deal/${deal.id}`)}>
                            <PipedriveIcon color="#25479A" />
                        </ButtonWithTooltip>
                        {!!deal.dropboxLink && (
                            <ButtonWithTooltip title="Dropbox" className={commonStyles.navigationButton} onClick={() => window.open(deal.dropboxLink!)}>
                                <DropboxIcon />
                            </ButtonWithTooltip>
                        )}
                        <ButtonWithTooltip title="Google Maps" className={commonStyles.navigationButton} onClick={() => window.open(`https://www.google.com/maps/place/${deal.lat},${deal.lon}`)}>
                            <GoogleMapsIcon />
                        </ButtonWithTooltip>
                        <BoeDownloadButton deal={deal} property={property} />
                        <LoiDownloadButton deal={deal} property={property} />
                    </div>
                </div>
                <SegmentedButtons
                    value={dealTab}
                    onChange={setDealTab}
                    options={segmentedButtons}
                    className={detailsStyles.segmentedButtons}
                />
                <div className={detailsStyles.body}>
                    {dealTab === DealTab.Details && <DealDetailsDetailsTab />}
                    {dealTab === DealTab.PropertyInfo && <DealDetailsPropertyTab />}
                    <DealDetailsCompaniesTab visible={dealTab === DealTab.Companies} setEntityCount={setCompanyCount} />
                    <DealDetailsLeaseCompsTab visible={dealTab === DealTab.LeaseComps} setEntityCount={setLeaseCompCount} />
                    <DealDetailsDealsTab visible={dealTab === DealTab.Deals} setEntityCount={setDealCount} />
                    <DealDetailsSaleCompsTab visible={dealTab === DealTab.SaleComps} setEntityCount={setSaleCompCount} />
                    <DealDetailsRecordedSalesTab visible={dealTab === DealTab.RecordedSales} setEntityCount={setRecordedSaleCount} />
                    <DealDetailsTenantsInMarketTab visible={dealTab === DealTab.TenantsInMarket} setEntityCount={setTenantInMarketCount} />
                </div>
            </DetailsPanelWithFlyTo>
            {isPortfolioPopupOpen && (
                <DealPortfolioPopup
                    dealId={deal.id}
                    portfolio={portfolio}
                    onClose={() => setIsPortfolioPopupOpen(false)}
                />
            )}
        </>
    );
}

export default observer(DealDetails);

enum DealTab {
    Details,
    PropertyInfo,
    Companies,
    LeaseComps,
    Deals,
    SaleComps,
    RecordedSales,
    TenantsInMarket,
}

const BoeDownloadButton = observer(function BoeDownloadButton({ deal, property }: IBoeDownloadButtonProps) {
    const { isGenerating, generateBoe } = useDealBoeGenerator(deal, property)
    const isDisabled = isGenerating || !property;

    return (
        <ButtonWithTooltip title="Download BOE" className={commonStyles.navigationButton} disabled={isDisabled} onClick={() => generateBoe()}>
            {isGenerating ? <Spinner /> : <TableConvertIcon color={isDisabled ? "#707070" : "#25479A"} />}
        </ButtonWithTooltip>
    );
})

interface IBoeDownloadButtonProps {
    deal: IDeal;
    property: IProperty | undefined;
}

const LoiDownloadButton = observer(function LoiDownloadButton({ deal, property }: ILoiDownloadButtonProps) {
    const { isGenerating, generateLoi } = useLoiGenerator(deal, property)
    const isDisabled = isGenerating || !property;

    return (
        <ButtonWithTooltip title="Download LOI" className={commonStyles.navigationButton} disabled={isDisabled} onClick={() => generateLoi()}>
            {isGenerating ? <Spinner /> : <ConvertToTextIcon color={isDisabled ? "#707070" : "#25479A"} />}
        </ButtonWithTooltip>
    );
})

interface ILoiDownloadButtonProps {
    deal: IDeal;
    property: IProperty | undefined;
}

